/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends HTMLElement {
  connectedCallback() {
    this.#normalizeOfficeMarkup();
    this.#mergeLineParagraphs();
    this.querySelectorAll('table').forEach(this.#formatTable);
  }

  #mergeLineParagraphs() {
    const isLine = (node) => {
      if (!node || node.tagName !== 'P') return false;
      if (node.querySelector('img, br, div, table, ul, ol, h1, h2, h3, h4, h5, h6')) return false;
      const text = node.textContent.trim();
      return text !== '' && text.length <= 60 && !/[.!?]$/.test(text);
    };

    const startsBlock = (node) => /:$/.test(node.textContent.trim());

    const paragraphs = [...this.querySelectorAll('p')];
    let index = 0;
    while (index < paragraphs.length) {
      if (!isLine(paragraphs[index])) {
        index += 1;
        continue;
      }
      const run = [paragraphs[index]];
      while (
        paragraphs[index + run.length] === run[run.length - 1].nextElementSibling &&
        isLine(paragraphs[index + run.length]) &&
        !startsBlock(paragraphs[index + run.length])
      ) {
        run.push(paragraphs[index + run.length]);
      }
      if (run.length > 1) {
        run[0].innerHTML = run.map((paragraph) => paragraph.innerHTML).join('<br>');
        run.slice(1).forEach((paragraph) => paragraph.remove());
      }
      index += run.length;
    }
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  #normalizeOfficeMarkup() {
    if (!this.querySelector('[style*="mso-"], [class*="Mso"]')) return;

    this.querySelectorAll('span[style*="mso-list: Ignore"]').forEach((node) => node.remove());

    this.querySelectorAll('span, font').forEach((node) => this.#unwrap(node));

    this.querySelectorAll('[style], [lang], [class]').forEach((node) => {
      node.removeAttribute('style');
      node.removeAttribute('lang');
      if (/(^|\s)Mso/.test(node.getAttribute('class') || '')) {
        node.removeAttribute('class');
      }
    });

    this.querySelectorAll('p').forEach((paragraph) => {
      const bold = this.#boldHeading(paragraph);
      if (bold) {
        const heading = document.createElement('h2');
        heading.innerHTML = bold.innerHTML;
        paragraph.replaceWith(heading);
      } else if (paragraph.textContent.trim() === '' && !paragraph.querySelector('img, br')) {
        paragraph.remove();
      }
    });
  }

  #unwrap(node) {
    const parent = node.parentNode;
    if (!parent) return;
    while (node.firstChild) parent.insertBefore(node.firstChild, node);
    parent.removeChild(node);
  }

  #boldHeading(paragraph) {
    if (paragraph.textContent.trim() === '') return null;
    const elements = [...paragraph.children].filter((element) => element.tagName !== 'BR');
    if (elements.length !== 1) return null;
    const bold = elements[0];
    if (bold.tagName !== 'B' && bold.tagName !== 'STRONG') return null;
    const hasStrayText = [...paragraph.childNodes].some(
      (node) => node !== bold && node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ''
    );
    return hasStrayText ? null : bold;
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
